export function stickyMode({ top, bottom, viewport, inset = 0, footer = Infinity, overlay = false, keyboard = false, hasForm = true, needsChoice = true }) {
  if (!hasForm || overlay || keyboard || footer < viewport + 80) return 'hidden';
  if (top >= viewport) return needsChoice ? 'choose' : 'add';
  if (bottom <= inset) return 'add';
  return 'hidden';
}

if (typeof window !== 'undefined' && !customElements.get('hoya-sticky-cart')) {
  const choices = new WeakMap();
  const overlaySelector = 'dialog[open], .drawer[open], .modal[open], x-drawer[open], x-modal[open], x-popover[open], cart-drawer[open], header-search[open], header-sidebar[open], quick-buy-modal[open], .pswp--open, .popup-modal.active, .popup-drawer.active, .modal.show, .hoya-search-drawer.is-open, .hoya-delivery.is-open, #vplugin._vp_toolbox_visible, #vplugin._vp_open_true';
  const selectedOptions = form => Array.from(form.elements).filter(input => input.matches('input[data-option-position]:checked')).sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)).map(input => input.value).join('|');

  customElements.define('hoya-sticky-cart', class extends HTMLElement {
    connectedCallback() {
      this.form = document.forms.namedItem(this.getAttribute('form'));
      this.action = this.querySelector('button');
      this.label = this.querySelector('[data-hoya-sticky-label]');
      if (!this.form || !this.action || !this.label) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.refresh(); });
      };
      this.action.addEventListener('click', () => this.activate(), options);
      window.addEventListener('scroll', this.schedule, { ...options, passive: true });
      window.addEventListener('resize', this.schedule, options);
      window.addEventListener('pageshow', this.schedule, options);
      window.visualViewport?.addEventListener('resize', this.schedule, options);
      document.addEventListener('focusin', this.schedule, options);
      document.addEventListener('focusout', this.schedule, options);
      document.addEventListener('change', event => {
        if (event.target.form === this.form && event.target.matches('[data-option-position]')) {
          choices.set(this.form, { expected: selectedOptions(this.form), pending: true });
          this.schedule();
        }
      }, { ...options, capture: true });
      document.addEventListener('variant:change', event => {
        if (event.target !== this.form) return;
        const state = choices.get(this.form);
        if (state && state.expected === selectedOptions(this.form)) state.pending = false;
        this.schedule();
      }, options);
      this.form.addEventListener('cart:error', () => this.showPurchase(), options);
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.schedule();
      });
      this.observer.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'class', 'hidden', 'disabled', 'aria-busy'] });
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.schedule);
        this.resizeObserver.observe(this);
      }
      this.refresh();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.releaseSpace();
    }

    releaseSpace() {
      document.documentElement.classList.remove('hoya-sticky-cart-visible');
      document.documentElement.style.removeProperty('--hoya-sticky-cart-space');
    }

    refresh() {
      if (!this.isConnected || !this.form || !this.action) return;
      this.mainButton = this.form.querySelector('button[type="submit"]');
      if (!this.mainButton) { this.hidden = true; this.releaseSpace(); return; }
      const viewport = window.visualViewport?.height || window.innerHeight;
      const bounds = this.mainButton.getBoundingClientRect();
      const header = document.querySelector('.shopify-section--header')?.getBoundingClientRect().bottom || 0;
      const focusedField = document.activeElement?.matches('input, textarea, select');
      const mode = stickyMode({
        top: bounds.top, bottom: bounds.bottom, viewport,
        inset: Math.max(0, Math.min(header, viewport / 2)),
        footer: document.querySelector('.shopify-section--footer')?.getBoundingClientRect().top,
        overlay: document.documentElement.classList.contains('lock') || Boolean(document.querySelector(overlaySelector)),
        keyboard: Boolean(focusedField && viewport < window.innerHeight * .8),
        needsChoice: this.dataset.needsChoice === 'true'
      });
      this.mode = mode;
      if (this.hidden !== (mode === 'hidden')) this.hidden = mode === 'hidden';
      const state = choices.get(this.form);
      const variantInput = this.form.elements.namedItem('id');
      const synced = variantInput && !variantInput.disabled && String(variantInput.value) === this.dataset.variantId && (!state || (!state.pending && state.expected === selectedOptions(this.form)));
      const busy = this.mainButton.getAttribute('aria-busy') === 'true';
      const disabled = mode === 'add' && (this.mainButton.disabled || busy || !synced);
      if (this.action.disabled !== disabled) this.action.disabled = disabled;
      const label = mode === 'choose' ? this.dataset.chooseLabel : this.mainButton.textContent.trim();
      if (this.label.textContent !== label) this.label.textContent = label;
      if (busy !== (this.action.getAttribute('aria-busy') === 'true')) {
        if (busy) this.action.setAttribute('aria-busy', 'true'); else this.action.removeAttribute('aria-busy');
      }
      if (this.hidden) { this.releaseSpace(); return; }
      const previewOffset = document.querySelector('#PBarNextFrame') ? 64 : 0;
      const space = `${Math.ceil(this.getBoundingClientRect().height) + previewOffset}px`;
      if (document.documentElement.style.getPropertyValue('--hoya-sticky-cart-space') !== space) document.documentElement.style.setProperty('--hoya-sticky-cart-space', space);
      if (!document.documentElement.classList.contains('hoya-sticky-cart-visible')) document.documentElement.classList.add('hoya-sticky-cart-visible');
    }

    showPurchase() {
      const picker = this.form.closest('product-rerender')?.querySelector(`variant-picker[form-id="${CSS.escape(this.getAttribute('form'))}"]`);
      const destination = picker || this.form;
      destination.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      const input = picker?.querySelector('input[data-option-position]:checked:not(:disabled)') || this.form.querySelector('input:not([type="hidden"]):not(:disabled), select, textarea') || this.mainButton;
      input?.focus({ preventScroll: true });
    }

    activate() {
      this.refresh();
      if (this.hidden || this.action.disabled) return;
      if (this.mode === 'choose') { this.showPurchase(); return; }
      if (!this.form.checkValidity()) { this.showPurchase(); this.form.reportValidity(); return; }
      this.form.requestSubmit(this.mainButton);
    }
  });
}
